import { FC } from 'react'
import moment from 'moment'
import { Select } from 'antd'
import { useTranslation } from 'react-i18next'
import './index.less'

const Option = Select.Option

export interface PerPaymentCycleDropDownProps {
  value: string | null
  earliestCycle?: string
  onChange: (value: string) => void
  disabled?: boolean
}

type CycleDateType = {
  disabledCycle: Date | null
  firstCycle: Date
  secondCycle: Date
  thirdCycle: Date
}
enum CYCLE {
  DISABLED = 'disabledCycle',
  FIRST = 'firstCycle',
  SECOND = 'secondCycle',
  THIRD = 'thirdCycle',
}

export const PerPaymentCycleDropDown: FC<PerPaymentCycleDropDownProps> = ({ value, earliestCycle, onChange, disabled, ...restProps }) => {
  const [t] = useTranslation()

  const currentValue = value !== null ? moment.utc(value).toDate() : null

  const firstCycleDate: Date = moment.utc(earliestCycle).toDate()
  const secondCycleDate: Date = moment.utc(earliestCycle).add(1, 'month').toDate()
  const thirdCycleDate: Date = moment.utc(earliestCycle).add(2, 'month').toDate()

  const cycleToDateMap: CycleDateType = {
    disabledCycle:
      currentValue !== null && (currentValue.getTime() < firstCycleDate.getTime() || currentValue.getTime() > thirdCycleDate.getTime())
        ? currentValue
        : null,
    firstCycle: firstCycleDate,
    secondCycle: secondCycleDate,
    thirdCycle: thirdCycleDate,
  }

  const getCycleToDateFromMap = (key: CYCLE): Date => {
    if (key === CYCLE.FIRST) {
      return cycleToDateMap.firstCycle
    } else if (key === CYCLE.SECOND) {
      return cycleToDateMap.secondCycle
    } else if (key === CYCLE.DISABLED && !!cycleToDateMap.disabledCycle) {
      return cycleToDateMap.disabledCycle
    }
    // else/default
    return cycleToDateMap.thirdCycle
  }

  const currentlySelectedOption = (): CYCLE | undefined => {
    if (value === null) {
      return undefined
    }
    const [currentYear, currentMonth] = [moment.utc(value).toDate().getUTCFullYear(), moment.utc(value).toDate().getUTCMonth()]
    if (
      !!cycleToDateMap.disabledCycle &&
      cycleToDateMap.disabledCycle.getUTCFullYear() === currentYear &&
      cycleToDateMap.disabledCycle.getUTCMonth() === currentMonth
    ) {
      return CYCLE.DISABLED
    } else if (firstCycleDate.getUTCFullYear() === currentYear && firstCycleDate.getUTCMonth() === currentMonth) {
      return CYCLE.FIRST
    } else if (secondCycleDate.getUTCFullYear() === currentYear && secondCycleDate.getUTCMonth() === currentMonth) {
      return CYCLE.SECOND
    } else if (thirdCycleDate.getUTCFullYear() === currentYear && thirdCycleDate.getUTCMonth() === currentMonth) {
      return CYCLE.THIRD
    } else {
      return CYCLE.FIRST
    }
  }

  const displayOption = (cycleName: CYCLE) => {
    const cycleDate: Date = getCycleToDateFromMap(cycleName)
    return `${t('global.month.' + cycleDate.getUTCMonth())} ${cycleDate.getUTCFullYear()} `
  }

  return (
    <>
      <Select
        optionFilterProp="children"
        onChange={(optionName: CYCLE) => onChange(getCycleToDateFromMap(optionName).toUTCString())}
        value={currentlySelectedOption()}
        placeholder={value === null ? t('monthPicker.placeholder') : undefined}
        className={currentlySelectedOption() === CYCLE.DISABLED ? 'error' : ''}
        {...restProps}
        style={value === null ? { width: '155px' } : undefined}
        disabled={disabled}
      >
        {!!cycleToDateMap.disabledCycle && (
          <Option value={CYCLE.DISABLED} disabled>
            {displayOption(CYCLE.DISABLED)}
          </Option>
        )}
        <Option value={CYCLE.FIRST}>{displayOption(CYCLE.FIRST)}</Option>
        <Option value={CYCLE.SECOND}>{displayOption(CYCLE.SECOND)}</Option>
        <Option value={CYCLE.THIRD}>{displayOption(CYCLE.THIRD)}</Option>
      </Select>
    </>
  )
}
